import { useLayoutEffect, useEffect, useRef, useState } from 'react';

// iPhone-Photos-style touch gestures for the gallery image viewer.
// Swipe left/right at 1x pages between images (a 3-pane strip follows the
// finger), pinch zooms anchored at the finger midpoint, double-tap toggles
// zoom at the tap point, and panning is clamped to the image edges.
// Zoom/pan math follows FloorplanMapView.jsx (translate+scale, origin 0 0),
// simplified because the zoom layer exactly covers its container.
//
// All per-frame values live in refs and are written straight to
// element.style inside a single rAF — React re-renders only at gesture
// boundaries (isZoomed) and on navigation commits.

// Mobile gate: the same UA regex this dialog already uses for fullScreen and
// its desktop-handler early-returns, plus iPadOS (desktop UA, multi-touch).
export const isMobileViewer =
  /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
    navigator.userAgent
  ) ||
  (navigator.maxTouchPoints > 1 && /Mac/i.test(navigator.userAgent));

export const PAGE_GUTTER = 24;

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const SLOP = 10; // px of movement before a touch commits to a gesture
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_RADIUS = 30;
const COMMIT_FRACTION = 0.4; // fraction of width a drag must cover to page
const FLICK_VELOCITY = 0.35; // px/ms
const EDGE_RESISTANCE = 0.3; // rubber-band ratio at first/last image
const SNAP_MS = 300;
const SNAP_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const ZOOM_MS = 280;
const ZOOM_EASING = 'cubic-bezier(0.2, 0, 0.2, 1)';
const UNDERZOOM_RESISTANCE = 0.4; // pinch below 1x moves the needle at 40%
const UNDERZOOM_FLOOR = 0.7;

// Interactive descendants of the gesture host (chevrons, action buttons,
// the video mute button) act on their own — touches starting on them never
// begin a gesture, so a fast double-press on a chevron can't trigger
// double-tap zoom. The <video> body itself is a first-class gesture surface
// (Reels-style: tap toggles playback, horizontal swipe pages) — the mobile
// pane renders no native controls, so there is no scrubber to hijack.
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select';

// Pan bound for one axis; c = container dim, r = rendered image dim at 1x.
// Layer transform is translate(t) scale(z) with origin 0 0 and a symmetric
// letterbox, so centering collapses to c(1-z)/2 and the edges pin as below.
function clampAxis(t, z, c, r) {
  if (z * r <= c) return (c * (1 - z)) / 2;
  const min = c - (z * (c + r)) / 2; // right/bottom edge pinned
  const max = (-z * (c - r)) / 2; // left/top edge pinned
  return Math.max(min, Math.min(max, t));
}

function touchDist(a, b) {
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

function findTouch(touchList, identifier) {
  for (let i = 0; i < touchList.length; i += 1) {
    if (touchList[i].identifier === identifier) return touchList[i];
  }
  return null;
}

export function useMobilePhotoGestures({
  stripRef,
  zoomLayerRef,
  imgRef,
  rotation,
  isVideo,
  canGoPrev,
  canGoNext,
  onNavigate,
  onVideoTap,
  currentId,
}) {
  const [isZoomed, setIsZoomed] = useState(false);

  const gestureRef = useRef({
    state: 'IDLE', // IDLE | TOUCHING | PAGING | PANNING | PINCHING | DEAD | ANIMATING
    touchId: null,
    startX: 0,
    startY: 0,
    box: null, // strip rect at gesture start
    rect: null, // rendered image rect {rw, rh} at gesture start
    panStart: { x: 0, y: 0 },
    pinch: null, // { dist, mid: {x, y} }
    zRaw: 1, // unresisted zoom while pinching (for under-zoom rubber)
    samples: [], // recent {t, x} touch samples for flick velocity
    lastTap: null, // { t, x, y }
  });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const dragXRef = useRef(0);
  const pendingCommitRef = useRef(null); // -1 | 1 while a page commit animates
  const lastLayerRef = useRef(null); // last zoom layer we wrote styles onto
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const animTokenRef = useRef(0);
  // Mirror rotation into a ref so long-lived closures (the window-level
  // pinch-end listener, ResizeObserver) never see a stale value.
  const rotationRef = useRef(rotation);
  rotationRef.current = rotation;

  const measureBox = () => {
    const el = stripRef.current;
    if (!el) return { left: 0, top: 0, width: 1, height: 1 };
    // Measure the untransformed parent — the strip itself may be mid-swipe
    // (translated), which would shift the rect by a full pane width.
    const b = (el.parentElement || el).getBoundingClientRect();
    return { left: b.left, top: b.top, width: b.width, height: b.height };
  };

  // On-screen extents of the contain-fitted image at 1x. The browser fits the
  // unrotated bitmap first and the rotate() then swaps the visual extents.
  const renderedRect = (box) => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    const s = Math.min(
      box.width / img.naturalWidth,
      box.height / img.naturalHeight
    );
    let rw = img.naturalWidth * s;
    let rh = img.naturalHeight * s;
    if (rotationRef.current % 180 !== 0) {
      const t = rw;
      rw = rh;
      rh = t;
    }
    return { rw, rh };
  };

  const clampPan = (x, y, z, box, rect) => {
    if (!rect) return { x: (box.width * (1 - z)) / 2, y: (box.height * (1 - z)) / 2 };
    return {
      x: clampAxis(x, z, box.width, rect.rw),
      y: clampAxis(y, z, box.height, rect.rh),
    };
  };

  const applyLayer = (transition) => {
    const el = zoomLayerRef.current;
    if (!el) return;
    lastLayerRef.current = el;
    el.style.transition = transition || 'none';
    el.style.transform = `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoomRef.current})`;
  };

  const applyStrip = (transition, x) => {
    const el = stripRef.current;
    if (!el) return;
    el.style.transition = transition || 'none';
    el.style.transform = `translateX(${x}px)`;
  };

  const scheduleWrite = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const g = gestureRef.current;
      if (g.state === 'PAGING') {
        applyStrip('none', dragXRef.current);
      } else if (g.state === 'PANNING' || g.state === 'PINCHING') {
        applyLayer('none');
      }
    });
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Refs always hold the animation's END values; the timer only finalizes.
  const startTimer = (ms, cb) => {
    clearTimer();
    animTokenRef.current += 1;
    const token = animTokenRef.current;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (animTokenRef.current === token) cb();
    }, ms);
  };

  const resetZoomImmediate = () => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    // Clear both the layer we last wrote onto (it may have become a side
    // pane after navigation — React never resets imperative styles) and the
    // current center layer.
    [lastLayerRef.current, zoomLayerRef.current].forEach((el) => {
      if (el) {
        el.style.transition = 'none';
        el.style.transform = '';
      }
    });
    lastLayerRef.current = zoomLayerRef.current;
    setIsZoomed(false);
  };

  const reset = () => {
    const g = gestureRef.current;
    clearTimer();
    animTokenRef.current += 1;
    pendingCommitRef.current = null;
    dragXRef.current = 0;
    applyStrip('none', 0);
    resetZoomImmediate();
    g.state = 'IDLE';
    g.lastTap = null;
    g.pinch = null;
  };

  // A touch during an animation jumps it to its end state so rapid flicking
  // stays robust; a pending page commit is executed synchronously (the
  // currentId layout effect below re-zeroes the strip before paint).
  const jumpToEnd = () => {
    clearTimer();
    animTokenRef.current += 1;
    const dir = pendingCommitRef.current;
    if (dir) {
      applyStrip('none', dragXRef.current);
      onNavigate(dir);
    } else {
      applyStrip('none', dragXRef.current);
      applyLayer('none');
    }
    gestureRef.current.state = 'IDLE';
  };

  const settleToIdentity = (animated) => {
    const g = gestureRef.current;
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setIsZoomed(false);
    if (animated) {
      applyLayer(`transform ${ZOOM_MS}ms ${ZOOM_EASING}`);
      g.state = 'ANIMATING';
      startTimer(ZOOM_MS + 20, () => {
        applyLayer('none');
        g.state = 'IDLE';
      });
    } else {
      applyLayer('none');
      g.state = 'IDLE';
    }
  };

  const doubleTapZoom = (clientX, clientY) => {
    const g = gestureRef.current;
    if (zoomRef.current > 1.01) {
      settleToIdentity(true);
      return;
    }
    const box = g.box || measureBox();
    const rect = renderedRect(box);
    if (!rect) {
      g.state = 'IDLE';
      return;
    }
    // iPhone heuristic: zoom so the image fills the letterboxed dimension;
    // if it already (nearly) fills the screen, use a plain 2x.
    const fill = Math.max(box.width / rect.rw, box.height / rect.rh);
    const target = Math.min(fill > 1.2 ? fill : 2, MAX_ZOOM);
    const mx = clientX - box.left;
    const my = clientY - box.top;
    zoomRef.current = target;
    panRef.current = clampPan(
      mx * (1 - target),
      my * (1 - target),
      target,
      box,
      rect
    );
    setIsZoomed(true);
    applyLayer(`transform ${ZOOM_MS}ms ${ZOOM_EASING}`);
    g.state = 'ANIMATING';
    startTimer(ZOOM_MS + 20, () => {
      applyLayer('none');
      gestureRef.current.state = 'IDLE';
    });
  };

  // Shared pinch finalizer: called from the synthetic touchend AND from the
  // window-level capture listener below — a pinch whose fingers started on an
  // overlay (which stopPropagation()s <2-touch events) never delivers its end
  // events to the gesture host, so the capture listener is the only way to
  // avoid the zoom sticking mid-rubber-band.
  const endPinch = (remainingTouches) => {
    const g = gestureRef.current;
    if (zoomRef.current < 1.05) {
      settleToIdentity(true);
    } else if (remainingTouches && remainingTouches.length === 1) {
      // Pinch hands off to a one-finger pan.
      const t = remainingTouches[0];
      g.state = 'PANNING';
      g.touchId = t.identifier;
      g.startX = t.clientX;
      g.startY = t.clientY;
      g.panStart = { ...panRef.current };
      setIsZoomed(true);
    } else {
      setIsZoomed(true);
      g.state = 'IDLE';
    }
    g.pinch = null;
  };

  const flickVelocity = () => {
    const samples = gestureRef.current.samples;
    if (samples.length < 2) return 0;
    const last = samples[samples.length - 1];
    let first = samples[0];
    for (let i = samples.length - 2; i >= 0; i -= 1) {
      if (last.t - samples[i].t <= 100) first = samples[i];
      else break;
    }
    const dt = last.t - first.t;
    return dt > 0 ? (last.x - first.x) / dt : 0;
  };

  const endPaging = () => {
    const g = gestureRef.current;
    const dx = dragXRef.current;
    const cw = g.box.width;
    const v = flickVelocity();
    let dir = null;
    if (dx < 0 && canGoNext && (-dx > COMMIT_FRACTION * cw || v < -FLICK_VELOCITY)) {
      dir = 1;
    } else if (
      dx > 0 &&
      canGoPrev &&
      (dx > COMMIT_FRACTION * cw || v > FLICK_VELOCITY)
    ) {
      dir = -1;
    }
    if (dir) {
      const targetX = dir === 1 ? -(cw + PAGE_GUTTER) : cw + PAGE_GUTTER;
      const remaining = Math.abs(targetX - dx);
      const speed = Math.abs(v);
      const duration =
        speed >= FLICK_VELOCITY
          ? Math.max(150, Math.min(SNAP_MS, remaining / speed))
          : SNAP_MS;
      dragXRef.current = targetX;
      pendingCommitRef.current = dir;
      applyStrip(`transform ${duration}ms ${SNAP_EASING}`, targetX);
      g.state = 'ANIMATING';
      startTimer(duration + 20, () => {
        if (pendingCommitRef.current) onNavigate(pendingCommitRef.current);
      });
    } else {
      dragXRef.current = 0;
      applyStrip(`transform ${SNAP_MS}ms ${SNAP_EASING}`, 0);
      g.state = 'ANIMATING';
      startTimer(SNAP_MS + 20, () => {
        applyStrip('none', 0);
        gestureRef.current.state = 'IDLE';
      });
    }
  };

  const onTouchStart = (e) => {
    const g = gestureRef.current;
    if (
      e.target &&
      e.target.closest &&
      e.target.closest(INTERACTIVE_SELECTOR)
    ) {
      return; // let buttons and native video controls act on their own
    }
    if (g.state === 'ANIMATING') jumpToEnd();
    if (e.touches.length === 1) {
      const t = e.touches[0];
      g.state = 'TOUCHING';
      g.touchId = t.identifier;
      g.startX = t.clientX;
      g.startY = t.clientY;
      g.box = measureBox();
      g.rect = renderedRect(g.box);
      g.samples = [{ t: e.timeStamp, x: t.clientX }];
    } else if (e.touches.length === 2 && !isVideo) {
      if (g.state === 'PAGING') return; // a swipe stays a swipe
      const a = e.touches[0];
      const b = e.touches[1];
      // Measure fresh: a pinch can start from IDLE (first touch swallowed by
      // an overlay), where any cached box may predate a fullscreen toggle.
      g.box = measureBox();
      g.rect = renderedRect(g.box);
      g.pinch = {
        dist: touchDist(a, b),
        mid: { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 },
      };
      g.zRaw = zoomRef.current;
      g.state = 'PINCHING';
      e.preventDefault();
    }
  };

  const onTouchMove = (e) => {
    const g = gestureRef.current;

    if (g.state === 'PINCHING' && e.touches.length >= 2) {
      e.preventDefault();
      const a = e.touches[0];
      const b = e.touches[1];
      const dist = touchDist(a, b);
      const mid = {
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
      };
      if (g.pinch && g.pinch.dist > 0) {
        const zPrev = zoomRef.current;
        g.zRaw *= dist / g.pinch.dist;
        let z;
        if (g.zRaw >= 1) {
          z = Math.min(g.zRaw, MAX_ZOOM);
        } else {
          // Under-zoom rubber band; springs back to 1 on release.
          z = Math.max(1 - (1 - g.zRaw) * UNDERZOOM_RESISTANCE, UNDERZOOM_FLOOR);
        }
        if (z < 1) {
          panRef.current = {
            x: (g.box.width * (1 - z)) / 2,
            y: (g.box.height * (1 - z)) / 2,
          };
        } else {
          // Keep the image point under the finger midpoint fixed.
          const midX = mid.x - g.box.left;
          const midY = mid.y - g.box.top;
          const lastMidX = g.pinch.mid.x - g.box.left;
          const lastMidY = g.pinch.mid.y - g.box.top;
          const wx = (lastMidX - panRef.current.x) / zPrev;
          const wy = (lastMidY - panRef.current.y) / zPrev;
          panRef.current = clampPan(
            midX - wx * z,
            midY - wy * z,
            z,
            g.box,
            g.rect
          );
        }
        zoomRef.current = z;
      }
      g.pinch = { dist, mid };
      scheduleWrite();
      return;
    }

    // Strictly track the primary finger — falling back to another touch
    // would teleport the strip by the distance between the two fingers.
    const t = findTouch(e.touches, g.touchId);
    if (!t) return;

    if (g.state === 'TOUCHING') {
      const dx = t.clientX - g.startX;
      const dy = t.clientY - g.startY;
      if (Math.abs(dx) > SLOP || Math.abs(dy) > SLOP) {
        if (zoomRef.current > 1.01) {
          g.state = 'PANNING';
          g.panStart = { ...panRef.current };
          g.startX = t.clientX;
          g.startY = t.clientY;
        } else if (Math.abs(dx) >= Math.abs(dy)) {
          g.state = 'PAGING';
        } else {
          g.state = 'DEAD';
        }
      }
    }

    if (g.state === 'PAGING') {
      e.preventDefault();
      let dx = t.clientX - g.startX;
      if ((dx > 0 && !canGoPrev) || (dx < 0 && !canGoNext)) {
        dx *= EDGE_RESISTANCE;
      }
      dragXRef.current = dx;
      g.samples.push({ t: e.timeStamp, x: t.clientX });
      if (g.samples.length > 6) g.samples.shift();
      scheduleWrite();
    } else if (g.state === 'PANNING') {
      e.preventDefault();
      panRef.current = clampPan(
        g.panStart.x + (t.clientX - g.startX),
        g.panStart.y + (t.clientY - g.startY),
        zoomRef.current,
        g.box,
        g.rect
      );
      scheduleWrite();
    }
  };

  const onTouchEnd = (e) => {
    const g = gestureRef.current;
    switch (g.state) {
      case 'TOUCHING': {
        if (e.touches.length > 0) {
          if (!findTouch(e.touches, g.touchId)) {
            // The tracked finger lifted while another stayed down —
            // re-baseline on the survivor instead of teleporting to it.
            const t = e.touches[0];
            g.touchId = t.identifier;
            g.startX = t.clientX;
            g.startY = t.clientY;
            g.samples = [{ t: e.timeStamp, x: t.clientX }];
          }
          break;
        }
        const t = e.changedTouches[0];
        if (isVideo) {
          // Reels-style: a clean tap on the video toggles play/pause,
          // immediately — double-tap zoom is image-only, so there is no
          // 300 ms wait. The duration guard filters press-and-hold.
          if (t && onVideoTap && e.timeStamp - g.samples[0].t < 500) {
            onVideoTap();
          }
          g.lastTap = null;
          g.state = 'IDLE';
          break;
        }
        const tap = g.lastTap;
        if (
          !isVideo &&
          t &&
          tap &&
          e.timeStamp - tap.t < DOUBLE_TAP_MS &&
          Math.hypot(t.clientX - tap.x, t.clientY - tap.y) < DOUBLE_TAP_RADIUS
        ) {
          g.lastTap = null;
          doubleTapZoom(t.clientX, t.clientY);
        } else {
          g.lastTap = t
            ? { t: e.timeStamp, x: t.clientX, y: t.clientY }
            : null;
          g.state = 'IDLE';
        }
        break;
      }
      case 'PAGING': {
        if (!findTouch(e.touches, g.touchId)) endPaging();
        break;
      }
      case 'PANNING': {
        if (e.touches.length === 0) g.state = 'IDLE';
        break;
      }
      case 'PINCHING': {
        if (e.touches.length >= 2) break;
        endPinch(e.touches);
        break;
      }
      case 'DEAD': {
        if (e.touches.length === 0) g.state = 'IDLE';
        break;
      }
      default:
        break;
    }
  };

  const onTouchCancel = () => {
    const g = gestureRef.current;
    if (g.state === 'ANIMATING') return; // let a pending commit finish
    if (g.state === 'PAGING') {
      dragXRef.current = 0;
      applyStrip(`transform ${SNAP_MS}ms ${SNAP_EASING}`, 0);
      g.state = 'ANIMATING';
      startTimer(SNAP_MS + 20, () => {
        applyStrip('none', 0);
        gestureRef.current.state = 'IDLE';
      });
      return;
    }
    if (g.state === 'PINCHING' && zoomRef.current < 1.05) {
      settleToIdentity(true);
      g.pinch = null;
      return;
    }
    if (zoomRef.current > 1.01) setIsZoomed(true);
    g.pinch = null;
    g.state = 'IDLE';
  };

  // Navigation commit / any image change: zero the strip and reset zoom
  // before paint, so the prefetched neighbor lands centered with no flash.
  useLayoutEffect(() => {
    const g = gestureRef.current;
    clearTimer();
    animTokenRef.current += 1;
    pendingCommitRef.current = null;
    dragXRef.current = 0;
    const strip = stripRef.current;
    if (strip) {
      strip.style.transition = 'none';
      strip.style.transform = 'translateX(0px)';
      void strip.offsetWidth; // flush so no old transition interpolates
    }
    resetZoomImmediate();
    // isVideo is FRESH here (this effect's closure comes from the render
    // that committed the new currentId), unlike in the touchstart handler
    // that ran jumpToEnd — a pinch seeded there against a stale !isVideo
    // must die now, or it would silently inflate zoomRef on a video center.
    if (g.state === 'TOUCHING' || (g.state === 'PINCHING' && !isVideo)) {
      // A touch that interrupted the page-commit animation is still down
      // (jumpToEnd ran onNavigate inside the touchstart handler, so this
      // effect fires after the new gesture was seeded). Keep it alive
      // against the new image instead of orphaning it in IDLE.
      g.box = measureBox();
      g.rect = renderedRect(g.box);
      if (g.state === 'PINCHING') g.zRaw = 1;
    } else {
      g.state = 'IDLE';
      g.pinch = null;
    }
    g.lastTap = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Touch events are delivered to the element the touch STARTED on. A pinch
  // whose fingers started on the comments/info overlays (which stop
  // propagation of <2-touch events) enters PINCHING via the 2-finger start
  // but never gets its end events through the gesture host — the capture
  // phase at window level sees them regardless. Runs before React's own
  // (document-level) dispatch, so the synthetic PINCHING branch simply finds
  // a non-PINCHING state afterwards and does nothing.
  useEffect(() => {
    const onWindowTouchEndCapture = (e) => {
      const g = gestureRef.current;
      if (g.state !== 'PINCHING' || e.touches.length >= 2) return;
      endPinch(e.touches);
    };
    window.addEventListener('touchend', onWindowTouchEndCapture, true);
    window.addEventListener('touchcancel', onWindowTouchEndCapture, true);
    return () => {
      window.removeEventListener('touchend', onWindowTouchEndCapture, true);
      window.removeEventListener('touchcancel', onWindowTouchEndCapture, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-clamp when the container resizes (orientation change, fullscreen).
  useEffect(() => {
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const g = gestureRef.current;
      if (g.state !== 'IDLE' || zoomRef.current <= 1) return;
      const box = measureBox();
      const rect = renderedRect(box);
      panRef.current = clampPan(
        panRef.current.x,
        panRef.current.y,
        zoomRef.current,
        box,
        rect
      );
      applyLayer('none');
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel },
    isZoomed,
    reset,
  };
}
